import "./FundRaise.css";
import React, { useState } from "react";
import { Typography } from "antd";
import {
  DollarCircleOutlined,
  GlobalOutlined,
  TeamOutlined,
  FundViewOutlined,
  StockOutlined,
  ProjectOutlined,
  ShopOutlined,
  BarChartOutlined,
  SafetyOutlined,
  RiseOutlined,
  SwapOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import Footer from "../../components/Footer/Footer";
import Navbar from "../../components/navbar/index.jsx";
import Container from "@mui/material/Container";
import { Grid, Box, TextField } from "@mui/material";
import fundraiseImage from "../../assets/images/fund-raise.jpg";
import image1 from "../../assets/images/stockmarket2.jpg";

const { Title } = Typography;

// Capital Solutions Data
const capitalSolutions = [
  {
    icon: <DollarCircleOutlined />,
    title: "Debt Funding",
    subtitle: "Growth Without Dilution",
    description: "For businesses with strong cash flows and operational stability, debt can accelerate growth without compromising equity.",
    supports: [
      "Structured Business Loans",
      "Institutional & NBFC Debt",
      "Working Capital & Expansion Funding",
      "Venture Debt",
      "Structured Debt Instruments"
    ],
    role: [
      "Financial readiness assessment",
      "Cash-flow risk mapping",
      "Debt capacity evaluation",
      "Lender positioning & negotiation"
    ]
  },
  {
    icon: <StockOutlined />,
    title: "Pre-IPO Funding",
    subtitle: "Preparing for the Public Market Journey",
    description: "Pre-IPO capital strengthens valuation, governance, and investor confidence before stepping into the public markets.",
    supports: [
      "Benchmark valuation against market comparables",
      "Refine financial governance",
      "Structure equity prior to listing",
      "Onboard strategic Pre-IPO investors"
    ]
  },
  {
    icon: <ProjectOutlined />,
    title: "Project Funding",
    subtitle: "Converting Vision into Fundable Opportunity",
    description: "Large-scale projects require structured feasibility, financial modeling, and capital mix optimization.",
    supports: [
      "DPR evaluation & restructuring",
      "Debt-Equity optimization",
      "Institutional & private capital alignment",
      "Risk mitigation frameworks"
    ]
  },
  {
    icon: <ShopOutlined />,
    title: "Private Equity Funding",
    subtitle: "Strategic Capital for Scale",
    description: "Private Equity is long-term partnership capital for growth-stage companies.",
    supports: [
      "Expansion capital structuring",
      "Minority & strategic equity deals",
      "Investor alignment & cap-table planning",
      "Valuation positioning",
      "Negotiation & term-sheet strategy"
    ]
  }
];

// AFO Journey Steps
const afoSteps = [
  {
    step: 1,
    title: "Business Deep Dive",
    description: "We begin by assessing your business the way institutional investors do.",
    points: [
      "Revenue & profitability diagnostics",
      "Risk exposure mapping",
      "Capital efficiency analysis",
      "Scalability assessment",
      "Realistic valuation benchmarking"
    ]
  },
  {
    step: 2,
    title: "Valuation & Structuring",
    description: "Once clarity is established, we structure the opportunity.",
    points: [
      "Financial model refinement",
      "Dilution planning",
      "Capital requirement optimization",
      "Investment deck restructuring",
      "Equity vs Debt strategy alignment"
    ]
  },
  {
    step: 3,
    title: "Investor Mapping",
    description: "Capital raising is about strategic alignment, not mass pitching.",
    points: [
      "Debt providers",
      "Private equity investors",
      "Strategic partners",
      "Pre-IPO investors",
      "Institutional capital pools"
    ]
  },
  {
    step: 4,
    title: "Deal Negotiation",
    description: "Term sheets define long-term impact.",
    points: [
      "Valuation negotiations",
      "Clause evaluation",
      "Deal structuring",
      "Documentation coordination",
      "Final capital closure strategy"
    ]
  },
  {
    step: 5,
    title: "Capital Closure",
    description: "Structured execution leading to successful capital closure.",
    points: [
      "Final term sheet execution",
      "Legal documentation",
      "Fund transfer coordination",
      "Post-closure compliance"
    ]
  },
  {
    step: 6,
    title: "Post-Funding Governance",
    description: "Funding is not the finish line — it is the beginning of responsibility.",
    points: [
      "Capital deployment planning",
      "Governance frameworks",
      "Investor reporting structure",
      "Performance milestone tracking",
      "Growth benchmarking"
    ]
  }
];

// Why AQT Features
const whyAQT = [
  {
    icon: <SafetyOutlined />,
    title: "Financial Discipline",
    description: "Rigorous financial analysis and disciplined approach to capital structuring"
  },
  {
    icon: <BarChartOutlined />,
    title: "Capital Structuring Expertise",
    description: "Deep expertise in structuring deals that align with your business stage"
  },
  {
    icon: <TeamOutlined />,
    title: "Investor Network Alignment",
    description: "Strategic alignment with the right investors for your specific needs"
  },
  {
    icon: <RiseOutlined />,
    title: "Governance-First Methodology",
    description: "Building sustainable growth through strong governance frameworks"
  },
  {
    icon: <SwapOutlined />,
    title: "Execution-Driven Engagement",
    description: "We don't just advise — we execute alongside your team"
  }
];

export default function FundRaise() {
  const [width] = useState(window.innerWidth);
  const navigate = useNavigate();

  const handleFormSubmit = (event) => {
    event.preventDefault();

    const formData = new FormData(event.target);
    const dataToSubmit = {};

    formData.forEach((value, key) => {
      dataToSubmit[key] = value;
    });

    fetch("https://api.aqtdirect.com/api/V1/global/fund-raising", {
      method: "POST",
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
      },
      body: JSON.stringify(dataToSubmit),
    })
      .then((res) => res.json())
      .then((res) => {
        console.log(res);
        if (res.success) {
          navigate("/thank-you");
        } else {
          alert("Error submitting form. Please try again.");
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        alert("Failed to submit form. Please check your network connection.");
      });
  };

  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <div className="fundraisetop">
        <div className="row">
          <div className="col-heading">
            <h1>FUNDRAISE WITH AQT DIRECT</h1>
            <p className="hero-subtitle">Capital. Structured. Positioned. Executed.</p>
          </div>
          <div className="col-content">
            <p className="hero-description">
              At AQT Direct, fundraising is not about sending decks to investors.<br />
              It is about understanding which capital fits your stage, structuring your 
              business to deserve it, and executing the deal with financial discipline.
            </p>
            <p className="hero-emphasis">
              Different businesses require different capital strategies.<br />
              We don't "arrange funds." We architect funding journeys.
            </p>
          </div>
        </div>
      </div>

      {/* Capital Solutions Section */}
      <div className="capital-solutions-container">
        <div className="section-header">
          <h2 className="header">Our Capital Solutions</h2>
          <p className="section-subheading">
            Tailored funding strategies for every stage of your business journey
          </p>
        </div>

        <div className="solutions-grid">
          {capitalSolutions.map((solution, index) => (
            <motion.div
              key={index}
              className="solution-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="solution-icon">{solution.icon}</div>
              <h3 className="solution-title">{solution.title}</h3>
              <p className="solution-subtitle">{solution.subtitle}</p>
              <p className="solution-description">{solution.description}</p>
              
              <div className="solution-supports">
                <h4>We support:</h4>
                <ul>
                  {solution.supports.map((item, idx) => (
                    <li key={idx}>
                      <CheckCircleOutlined className="check-icon" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {solution.role && (
                <div className="solution-role">
                  <h4>Our role includes:</h4>
                  <ul>
                    {solution.role.map((item, idx) => (
                      <li key={idx}>
                        <CheckCircleOutlined className="check-icon" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* AFO Section */}
      <div className="afo-section">
        <div className="afo-header">
          <h2 className="afo-title">Introducing</h2>
          <h3 className="afo-subtitle">Hire Your AQT Financial Officer (AFO)</h3>
          <p className="afo-description">
            Your Dedicated Capital Strategist — From Start to Finish
          </p>
          <div className="afo-highlight">
            <p>
              Many businesses fail to raise funds not due to lack of investors —<br />
              but due to lack of structured financial leadership.
            </p>
            <p className="afo-emphasis">
              With the AQT Financial Officer Model, your company gets a dedicated capital 
              expert who works alongside your leadership team until funding is secured 
              and deployed strategically.
            </p>
            <p className="afo-tagline">This is beyond advisory. This is execution partnership.</p>
          </div>
        </div>

        <div className="afo-journey">
          <h3 className="journey-title">The AQT Financial Officer Journey</h3>
          <div className="journey-flow">
            {afoSteps.map((step, index) => (
              <motion.div
                key={index}
                className="journey-step"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="step-number">{step.step}</div>
                <div className="step-content">
                  <h4>{step.title}</h4>
                  <p>{step.description}</p>
                  <ul>
                    {step.points.map((point, idx) => (
                      <li key={idx}>
                        <CheckCircleOutlined className="check-icon-small" />
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Who Should Engage Section */}
      <div className="target-audience">
        <div className="section-header">
          <h2 className="header">Who Should Engage with AQT?</h2>
        </div>
        <div className="audience-grid">
          <div className="audience-item">
            <CheckCircleOutlined className="audience-icon" />
            <p>Growth-stage businesses seeking expansion</p>
          </div>
          <div className="audience-item">
            <CheckCircleOutlined className="audience-icon" />
            <p>Companies preparing for IPO</p>
          </div>
          <div className="audience-item">
            <CheckCircleOutlined className="audience-icon" />
            <p>Capital-intensive project owners</p>
          </div>
          <div className="audience-item">
            <CheckCircleOutlined className="audience-icon" />
            <p>Founders needing structured financial leadership</p>
          </div>
          <div className="audience-item">
            <CheckCircleOutlined className="audience-icon" />
            <p>Businesses looking for strategic private equity</p>
          </div>
        </div>
        <p className="audience-message">
          If you are serious about scaling — your capital strategy must be serious too.
        </p>
      </div>

      {/* Why AQT Section */}
      <div className="why-aqt-section">
        <div className="section-header">
          <h2 className="header">Why AQT Direct?</h2>
        </div>
        <div className="why-grid">
          {whyAQT.map((item, index) => (
            <motion.div
              key={index}
              className="why-card"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="why-icon">{item.icon}</div>
              <h4>{item.title}</h4>
              <p>{item.description}</p>
            </motion.div>
          ))}
        </div>
        <p className="why-conclusion">
          We do not operate as intermediaries. We operate as capital strategists.
        </p>
      </div>

      {/* CTA Section */}
      <motion.section
        className="ready-to-raise"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexWrap: "wrap",
          backgroundColor: "white",
          padding: "40px 20px",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
          gap: "40px",
        }}
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <img
          src={image1}
          alt="Fundraising"
          style={{
            width: "100%",
            maxWidth: "300px",
            height: "auto",
            borderRadius: "12px",
          }}
        />

        <div style={{ textAlign: "left", maxWidth: "600px" }}>
          <h2 className="raise-header">
            Ready to Structure Your Capital Journey?
          </h2>
          <p className="raise-description">
            Whether you require Debt Funding, Pre-IPO capital, Project Financing, 
            Private Equity, or a Dedicated AQT Financial Officer — we help you move 
            from intention to execution.
          </p>
          <p className="raise-emphasis">
            Capital is available.<br />
            The real question is — are you structured for it?
          </p>
        </div>
      </motion.section>

      {/* Contact Form Section */}
      <Container
        className="contact-main-container"
        style={{
          backgroundImage: `url(${fundraiseImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          padding: "50px 20px",
          position: "relative",
          color: "#fff",
          width: "100vw",
          maxWidth: "100%",
          backgroundColor: "#097b72",
          marginTop: "0px",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            zIndex: 1,
          }}
        />

        <Grid container spacing={4} style={{ position: "relative", zIndex: 2 }}>
          <Grid item xs={12} md={6}>
            <Box
              className="left-section"
              style={{
                marginLeft: width > 1080 ? "150px" : "59px",
              }}
            >
              <div className="typography-heading">
                Start Your Capital Journey Today
              </div>
              <p className="fund-description">
                Take the first step towards structured fundraising. Our team of 
                capital strategists will guide you through every phase of the journey.
              </p>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              className="form-container"
              style={{
                padding: "20px",
                borderRadius: "15px",
                boxShadow: "0 8px 15px rgba(0, 0, 0, 0.15)",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.transform = "scale(1.02)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.transform = "scale(1)")
              }
            >
              <form method="post" action="#" onSubmit={handleFormSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Full Name"
                          name="fullName"
                          required
                          variant="outlined"
                          size="small"
                          sx={{
                            backgroundColor: "#ffffff",
                            borderRadius: "5px",
                            "& fieldset": { border: "none" },
                            "& .MuiOutlinedInput-root": {
                              borderBottom: "2px solid #ccc",
                              "&:hover": { borderBottom: "2px solid #0b837a" },
                              "&.Mui-focused": { borderBottom: "2px solid #0b837a" },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Mobile Number"
                          type="tel"
                          name="phoneNo"
                          required
                          variant="outlined"
                          size="small"
                          sx={{
                            backgroundColor: "#ffffff",
                            borderRadius: "5px",
                            "& fieldset": { border: "none" },
                            "& .MuiOutlinedInput-root": {
                              borderBottom: "2px solid #ccc",
                              "&:hover": { borderBottom: "2px solid #0b837a" },
                              "&.Mui-focused": { borderBottom: "2px solid #0b837a" },
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Email Address"
                          type="email"
                          name="email"
                          required
                          variant="outlined"
                          size="small"
                          sx={{
                            backgroundColor: "#ffffff",
                            borderRadius: "5px",
                            "& fieldset": { border: "none" },
                            "& .MuiOutlinedInput-root": {
                              borderBottom: "2px solid #ccc",
                              "&:hover": { borderBottom: "2px solid #0b837a" },
                              "&.Mui-focused": { borderBottom: "2px solid #0b837a" },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Company Name"
                          type="text"
                          name="companyName"
                          required
                          variant="outlined"
                          size="small"
                          sx={{
                            backgroundColor: "#ffffff",
                            borderRadius: "5px",
                            "& fieldset": { border: "none" },
                            "& .MuiOutlinedInput-root": {
                              borderBottom: "2px solid #ccc",
                              "&:hover": { borderBottom: "2px solid #0b837a" },
                              "&.Mui-focused": { borderBottom: "2px solid #0b837a" },
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Turnover"
                          name="turnover"
                          required
                          variant="outlined"
                          size="small"
                          sx={{
                            backgroundColor: "#ffffff",
                            borderRadius: "5px",
                            "& fieldset": { border: "none" },
                            "& .MuiOutlinedInput-root": {
                              borderBottom: "2px solid #ccc",
                              "&:hover": { borderBottom: "2px solid #0b837a" },
                              "&.Mui-focused": { borderBottom: "2px solid #0b837a" },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Industry"
                          name="industry"
                          required
                          variant="outlined"
                          size="small"
                          sx={{
                            backgroundColor: "#ffffff",
                            borderRadius: "5px",
                            "& fieldset": { border: "none" },
                            "& .MuiOutlinedInput-root": {
                              borderBottom: "2px solid #ccc",
                              "&:hover": { borderBottom: "2px solid #0b837a" },
                              "&.Mui-focused": { borderBottom: "2px solid #0b837a" },
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Capital Requirements"
                      name="fundingRequirements"
                      required
                      multiline
                      rows={2}
                      variant="outlined"
                      size="small"
                      sx={{
                        backgroundColor: "#ffffff",
                        borderRadius: "5px",
                        "& fieldset": { border: "none" },
                        "& .MuiOutlinedInput-root": {
                          borderBottom: "2px solid #ccc",
                          "&:hover": { borderBottom: "2px solid #0b837a" },
                          "&.Mui-focused": { borderBottom: "2px solid #0b837a" },
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Interested In (Debt/Pre-IPO/Project/PE/AFO)"
                      name="interest"
                      required
                      variant="outlined"
                      size="small"
                      sx={{
                        backgroundColor: "#ffffff",
                        borderRadius: "5px",
                        "& fieldset": { border: "none" },
                        "& .MuiOutlinedInput-root": {
                          borderBottom: "2px solid #ccc",
                          "&:hover": { borderBottom: "2px solid #0b837a" },
                          "&.Mui-focused": { borderBottom: "2px solid #0b837a" },
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      style={{
                        background: "linear-gradient(90deg, #0b837a, #9ddad5)",
                        color: "#fff",
                        padding: "12px 20px",
                        fontWeight: "bold",
                        borderRadius: "25px",
                        boxShadow: "0 4px 10px rgba(0, 0, 0, 0.2)",
                        transition: "transform 0.3s ease",
                        fontSize: "16px",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.transform = "scale(1.05)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.transform = "scale(1)")
                      }
                      fullWidth
                    >
                      START YOUR CAPITAL JOURNEY
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Box>
          </Grid>
        </Grid>
      </Container>

      <Footer />
    </>
  );
}